/**
 * Which filter chips fit on one line, from widths measured once rather than from the live layout.
 *
 * The layout cannot be asked directly, because the answer changes it: hide a chip to make room and
 * the row is narrower, which makes the next reading disagree with the last. Measuring every chip
 * once, while they are all on screen, and computing against a width that does not depend on any of
 * them, gives an answer that settles.
 */

/** Width held back for the "+N" chip, so counting stops before it has nowhere to go. */
export const OVERFLOW_CHIP_WIDTH = 64;

/**
 * @param {string[]} keys - Every filter, in render order.
 * @param {Map<string, number>} widths - Each filter's width, measured while all were shown.
 * @param {number} available - The row's width, which does not change when a chip is hidden.
 * @param {number} gap - Space between chips.
 * @returns {string[]} The filters that fit, in order. All of them when none has to go.
 */
export function visibleFilterKeys(keys, widths, available, gap) {
  const measured = keys.reduce((sum, key) => sum + (widths.get(key) ?? 0), 0);

  // N chips carry N-1 gaps, not N. Counting the one after the last chip cost a row up to a gap's
  // width, so a set that fit exactly still collapsed into a count.
  const total = measured + Math.max(keys.length - 1, 0) * gap;
  if (total <= available) return [...keys];

  /*
   * `used` already carries a gap for each chip placed, and the last of those is the gap before the
   * overflow chip. Holding a further gap back here reserved that same space twice.
   */
  const limit = available - OVERFLOW_CHIP_WIDTH;
  const visible = [];
  let used = 0;

  keys.every((key) => {
    used += (widths.get(key) ?? 0) + gap;
    if (used > limit) return false;
    visible.push(key);
    return true;
  });

  return visible;
}
